import {useMemo, useState} from 'react';

import {FormControlLabel, FormLabel, Grid, RadioGroup} from '@mui/material';
import {
  Stack,
} from '@zoom/react-ui';
import {IconLocationGPS, IconLocationGrantFill, IconMSTeamsYes, IconPhone} from '@zoom/sun-ui-icon';
import {useTranslation} from 'react-i18next';

import {ReactComponent as GoogleMeetIcon} from 'assets/svg/google-meet.svg';
import {ReactComponent as ZoomAppIcon} from 'assets/svg/zoom-app-icon.svg';
import {TypographyErrorMsg} from 'Components/BookingBlocksWidget/Style';
import CountryInput from 'Components/CountryInput';
import {ZmTextField} from 'Components/ZmInputs';
import {ZmRadio} from 'Components/ZmRadio';
import {ADD_ON_TYPE} from 'Utils/consts';

const findSelectedIndex = (configurations, value) => {
  return `${configurations.findIndex((cfg) => {
    if ([ADD_ON_TYPE.CUSTOM, ADD_ON_TYPE.IN_PERSON].includes(cfg.kind)) {
      return value?.location === cfg.location;
    } else {
      return value?.kind === cfg.kind;
    }
  })}`;
};


export const LocationRadioSelector = ({configurations = [], errors, onChange, value}) => {
  const {t} = useTranslation();
  const sortedConfigurations = useMemo(() => {
    // `ask invitee` option should be displayed at the end
    return [...configurations].sort((a, b) => {
      if (a.kind === ADD_ON_TYPE.ASK_INVITEE) {
        return 1;
      } else if (b.kind === ADD_ON_TYPE.ASK_INVITEE) {
        return -1;
      }
      return 0;
    });
  }, [configurations]);

  const [selectedIndex, setSelectedIndex] = useState(findSelectedIndex(sortedConfigurations, value));
  const onlyOneLocation = configurations.length === 1;

  const label = useMemo(() => {
    let label = '';
    if (onlyOneLocation) {
      if (configurations[0].kind === ADD_ON_TYPE.OUT_BOUND_CALL) {
        label = 'booking.phoneNumber';
      } else if (configurations[0].kind === ADD_ON_TYPE.ASK_INVITEE) {
        label = 'apptForm.location';
      }
    } else if (configurations.length > 1) {
      label = 'apptForm.location';
    }
    return label;
  }, [onlyOneLocation, configurations]);

  return label? <>
    <FormLabel required sx={{marginTop: '12px !important', fontWeight: 500, fontSize: '.875rem'}}>
      {t(label)}
    </FormLabel>
    <RadioGroup
      row
      value={selectedIndex}
      onChange={(e) => {
        const idx = e.target.value;
        const cfg = sortedConfigurations[idx];
        setSelectedIndex(idx);
        onChange({
          kind: cfg.kind,
          location: cfg.location || '',
        });
      }}
      role={'radiogroup'}
    >
      <Stack direction="column" alignItems="left" sx={{width: '100%', gap: '.25rem'}}>
        {sortedConfigurations.map((cfg, idx) => {
          const idxStr = `${idx}`;
          // if no location or only one location, no need to choose
          return <>{configurations.length > 1 &&
            <FormControlLabel
              value={idxStr}
              control={<ZmRadio id={`location-option-${idxStr}`} />}
              label={
                <FormLabel
                  htmlFor={`location-option-${idxStr}`}
                  sx={{color: '#0A2540', fontWeight: 400, fontSize: '.875rem'}}
                >
                  <span>
                    {cfg.kind === ADD_ON_TYPE.IN_PERSON && <Stack direction="row" alignItems="center" gap=".3125rem">
                      <IconLocationGPS size={'.875rem'} />  {cfg.location}
                    </Stack>}
                    {(cfg.kind === ADD_ON_TYPE.IN_BOUND_CALL || cfg.kind === ADD_ON_TYPE.OUT_BOUND_CALL) &&
                      <Stack direction="row" alignItems="center" gap=".3125rem">
                        <IconPhone size={'.875rem'} /> {t('apptForm.phoneCall')}
                      </Stack>
                    }
                    {cfg.kind === ADD_ON_TYPE.ZOOM_MEETING && <Stack direction="row" alignItems="center" gap=".3125rem">
                      <ZoomAppIcon style={{width: '1rem', height: '1rem'}} /> {t('common.zoomMeeting')}
                    </Stack>}
                    {cfg.kind === ADD_ON_TYPE.GOOGLE_MEETING &&
                    <Stack direction="row" alignItems="center" gap=".3125rem">
                      <GoogleMeetIcon style={{width: '1rem', height: '1rem'}} /> {t('integration.googleMeetTitle')}
                    </Stack>}
                    {cfg.kind === ADD_ON_TYPE.TEAMS_MEETING &&
                    <Stack direction="row" alignItems="center" gap=".3125rem">
                      <IconMSTeamsYes style={{width: '1rem', height: '1rem'}} /> {t('integration.teamsMeetTitle')}
                    </Stack>}
                    {cfg.kind === ADD_ON_TYPE.CUSTOM && cfg.location}
                    {cfg.kind === ADD_ON_TYPE.ASK_INVITEE && <Stack direction="row" alignItems="center" gap=".3125rem">
                      <IconLocationGrantFill size={'.875rem'} /> {t('booking.somewhereElse')}
                    </Stack>}
                  </span>
                </FormLabel>
              }
            />
          }
          {cfg.kind === ADD_ON_TYPE.IN_BOUND_CALL && selectedIndex === idxStr &&
            <div style={{fontSize: '.75rem', color: '#777', marginLeft: '42px'}}>
              {t('booking.inviteeCallHostDesc')}
            </div>
          }
          {cfg.kind === ADD_ON_TYPE.OUT_BOUND_CALL && (selectedIndex === idxStr || onlyOneLocation) &&
            <Grid container spacing={2}>
              <Grid item style={{width: '140px'}}>
                <CountryInput
                  id={'outboundPhoneCountryCode'}
                  hideLabel
                  value={value.outboundPhoneCountryCode}
                  onChange={(val) => onChange({
                    outboundPhoneCountryCode: val,
                  })}
                  aria-live="polite"
                />
              </Grid>
              <Grid item xs>
                <ZmTextField margin="dense" id="outboundPhoneNumber" type="text" fullWidth aria-required
                  value={value.outboundPhoneNumber}
                  variant="outlined"
                  onChange={(e) => onChange({
                    outboundPhoneNumber: e.target.value.replace(/[^\d]/g, ''),
                  })}
                  inputProps={{
                    autoComplete: 'tel-national',
                    minLength: 5,
                    maxLength: 20,
                  }}
                  aria-live="polite"
                  error={!!errors?.locationConfiguration?.outboundPhoneNumber}
                  helperText={errors?.locationConfiguration?.outboundPhoneNumber?.message}
                />
              </Grid>
            </Grid>
          }
          {cfg.kind === ADD_ON_TYPE.ASK_INVITEE && (selectedIndex === idxStr || onlyOneLocation) &&
            <ZmTextField
              margin="dense" type="text" fullWidth variant="outlined" aria-required
              sx={configurations.length > 1 && {margin: '0 0 0 22px', width: '280px'}}
              size={configurations.length > 1? 'small': 'medium'}
              value={value.location}
              onChange={(e) => onChange({
                location: e.target.value,
              })}
              aria-live="polite"
              error={!!errors?.locationConfiguration?.location}
              helperText={errors?.locationConfiguration?.location?.message}
            />}
          </>;
        })}

        {errors && <TypographyErrorMsg role="alert" aria-live="polite" sx={{marginTop: '4px'}}>
          {errors?.locationConfiguration?.kind?.message}
        </TypographyErrorMsg>}
      </Stack>
    </RadioGroup>
  </>: null;
};
